/**
 * Mentor · Dijital Mentor
 *
 * worker/src/handlers/mentor.ts'in Mini App muadili. Section'lar peş peşe:
 *  - Hero: mentorSummary (tier, accuracy bar, totalAttempts, last7days, streak, srs)
 *  - Coach tip strip (mentorCoach.tips[0])
 *  - Bugünün Koçu (mentorCoach full)
 *  - Çalışma Deseni (mentorStudyPattern)
 *  - Forgetting Curve (mentorForgetting)
 *  - Sınav Hazırlık Skoru (mentorExamReadiness · locked olabilir)
 *  - Akran Karşılaştırma (mentorPeerCompare · locked olabilir)
 *  - Tükenmişlik (mentorBurnout · locked olabilir)
 *  - Zayıf Konular (weakTopics → /question?topicId=X)
 *
 * Bot'un kullandığı sparkline/heatmap chart util'i web'e tek tek SVG/Box ile
 * minik visual olarak yeniden örüldü; harici chart kütüphanesi yok.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Progress,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  type MentorBurnoutResp,
  type MentorCoachResp,
  type MentorExamReadinessResp,
  type MentorForgettingResp,
  type MentorPeerCompareResp,
  type MentorStudyPatternResp,
  type MentorSummaryResp,
} from '../lib/api';
import { haptic } from '../telegram';
import { BRAND, ICON } from '@algobot/shared';
import PlanBadge from '../components/PlanBadge';

interface MentorState {
  summary: MentorSummaryResp | null;
  coach: MentorCoachResp | null;
  pattern: MentorStudyPatternResp | null;
  forgetting: MentorForgettingResp | null;
  exam: MentorExamReadinessResp | null;
  peer: MentorPeerCompareResp | null;
  burnout: MentorBurnoutResp | null;
}

const initialState: MentorState = {
  summary: null,
  coach: null,
  pattern: null,
  forgetting: null,
  exam: null,
  peer: null,
  burnout: null,
};

export default function MentorPage() {
  const nav = useNavigate();
  const [data, setData] = useState<MentorState>(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const settle = await Promise.allSettled([
        api.mentorSummary(),
        api.mentorCoach(),
        api.mentorStudyPattern(),
        api.mentorForgetting(),
        api.mentorExamReadiness(),
        api.mentorPeerCompare(),
        api.mentorBurnout(),
      ]);
      const [summary, coach, pattern, forgetting, exam, peer, burnout] = settle;
      setData({
        summary: summary.status === 'fulfilled' ? summary.value : null,
        coach: coach.status === 'fulfilled' ? coach.value : null,
        pattern: pattern.status === 'fulfilled' ? pattern.value : null,
        forgetting: forgetting.status === 'fulfilled' ? forgetting.value : null,
        exam: exam.status === 'fulfilled' ? exam.value : null,
        peer: peer.status === 'fulfilled' ? peer.value : null,
        burnout: burnout.status === 'fulfilled' ? burnout.value : null,
      });
      // Hiçbir endpoint başarısız değilse temiz; summary boşsa hata göster
      if (summary.status === 'rejected') {
        const reason: any = summary.reason;
        if (reason?.status === 401) setError({ status: 401, message: 'Telegram içinde aç.' });
        else setError({ message: 'Mentor verisi alınamadı.' });
      }
    } catch (err: any) {
      setError({ status: err?.status, message: 'Mentor verisi alınamadı.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <Container maxW="md" py={10}>
        <VStack spacing={3}>
          <Spinner size="xl" color="accent" thickness="3px" />
          <Text color="textSecondary" fontSize="sm">Mentor verisi yükleniyor…</Text>
        </VStack>
      </Container>
    );
  }

  if (error && !data.summary) {
    return (
      <Container maxW="md" py={6}>
        <Alert status="warning" borderRadius="md" flexDirection="column" alignItems="flex-start">
          <HStack mb={2}>
            <AlertIcon />
            <Text fontWeight="bold">Bağlanılamadı</Text>
          </HStack>
          <Text fontSize="sm">{error.message}</Text>
        </Alert>
        <HStack mt={4}>
          <Button colorScheme="brand" onClick={load}>Tekrar dene</Button>
          <Button variant="outline" onClick={() => nav('/')}>Ana sayfa</Button>
        </HStack>
      </Container>
    );
  }

  const tipText = data.coach?.tips?.[0]?.text;

  return (
    <Container maxW="md" py={3} px={3}>
      <VStack spacing={3} align="stretch">
        {/* HERO · summary */}
        {data.summary ? <SummaryHero s={data.summary} /> : null}

        {/* COACH TIP STRIP */}
        {tipText ? (
          <Box
            bg="headerBg"
            borderWidth="1px"
            borderColor="accent"
            borderRadius="xl"
            boxShadow="cardElevated"
            p={3}
          >
            <HStack spacing={2} align="start">
              <Text fontSize="lg" lineHeight="1" color="accent" fontWeight="bold" pt={0.5}>
                {ICON.bullet}
              </Text>
              <Text fontSize="sm" color="textPrimary" lineHeight="1.4">
                {tipText}
              </Text>
            </HStack>
          </Box>
        ) : null}

        {/* COACH SECTION */}
        <Section
          glyph={ICON.bullet}
          glyphColor="accent"
          title="Bugünün Koçu"
        >
          {data.coach ? <CoachBlock coach={data.coach} /> : <UnavailableLine />}
        </Section>

        {/* STUDY PATTERN */}
        <Section
          glyph={BRAND.emojiContent.algospot}
          glyphColor="warningIcon"
          title="Çalışma Deseni"
          subtitle="Son 14 gün"
        >
          {data.pattern ? <PatternBlock p={data.pattern} /> : <UnavailableLine />}
        </Section>

        {/* FORGETTING CURVE */}
        <Section
          glyph={ICON.srs}
          glyphColor="successIcon"
          title="Forgetting Curve"
        >
          {data.forgetting ? (
            <ForgettingBlock f={data.forgetting} onReview={() => nav('/trainer')} />
          ) : (
            <UnavailableLine />
          )}
        </Section>

        {/* EXAM READINESS */}
        <Section
          glyph={ICON.star}
          glyphColor="accent"
          title="Sınav Hazırlık Skoru"
        >
          {data.exam ? (
            <ExamReadinessBlock r={data.exam} onUpgrade={() => nav('/subscription')} />
          ) : (
            <UnavailableLine />
          )}
        </Section>

        {/* PEER COMPARE */}
        <Section
          glyph={ICON.payment}
          glyphColor="coachAccent"
          title="Akran Karşılaştırma"
        >
          {data.peer ? (
            <PeerBlock p={data.peer} onUpgrade={() => nav('/subscription')} />
          ) : (
            <UnavailableLine />
          )}
        </Section>

        {/* BURNOUT */}
        <Section
          glyph={ICON.warn}
          glyphColor="warningIcon"
          title="Tükenmişlik"
        >
          {data.burnout ? (
            <BurnoutBlock b={data.burnout} onUpgrade={() => nav('/subscription')} />
          ) : (
            <UnavailableLine />
          )}
        </Section>

        {/* WEAK TOPICS */}
        {data.summary && data.summary.weakTopics?.length > 0 ? (
          <Section
            glyph={ICON.scope}
            glyphColor="accent"
            title="Zayıf Konular"
          >
            <VStack align="stretch" spacing={2}>
              {data.summary.weakTopics.map((t) => (
                <Box
                  key={t.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  bg="bgSecondary"
                  borderWidth="1px"
                  borderColor="borderPrimary"
                  borderRadius="lg"
                  px={3}
                  py={2}
                  _hover={{ borderColor: 'accent' }}
                  onClick={() => {
                    haptic('select');
                    nav(`/question?topicId=${t.id}`);
                  }}
                >
                  <HStack justify="space-between" align="center" spacing={2}>
                    <VStack align="start" spacing={0.5} minW={0} flex="1">
                      <Text fontSize="sm" fontWeight="semibold" color="textPrimary" noOfLines={1}>
                        {t.name}
                      </Text>
                      <Text fontSize="11px" color="textMuted">
                        {t.attempts} deneme · %{t.accuracy}
                      </Text>
                    </VStack>
                    <Box flex="0 0 110px">
                      <Progress
                        value={t.accuracy}
                        size="xs"
                        colorScheme={t.accuracy < 50 ? 'red' : t.accuracy < 75 ? 'yellow' : 'green'}
                        borderRadius="full"
                      />
                    </Box>
                    <Text color="textMuted">{ICON.forward}</Text>
                  </HStack>
                </Box>
              ))}
            </VStack>
            <Text fontSize="11px" color="textMuted" pt={2} fontStyle="italic">
              Bir konuya dokun → o konudan rastgele sorular aç.
            </Text>
          </Section>
        ) : null}

        <Button variant="ghost" onClick={() => nav('/')}>
          {BRAND.menuLabels.home}
        </Button>
      </VStack>
    </Container>
  );
}

// ──────────────────────────────────────────────────────────
// Section wrapper
// ──────────────────────────────────────────────────────────

function Section({
  glyph,
  glyphColor,
  title,
  subtitle,
  children,
}: {
  glyph: string;
  glyphColor: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      bg="bgCard"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="xl"
      boxShadow="cardElevated"
      p={3.5}
    >
      <HStack spacing={2} mb={2.5} align="center">
        <Text fontSize="lg" lineHeight="1" color={glyphColor} fontWeight="bold">
          {glyph}
        </Text>
        <VStack align="start" spacing={0}>
          <Text fontWeight="bold" color="textPrimary" fontSize="sm">{title}</Text>
          {subtitle ? <Text fontSize="11px" color="textMuted">{subtitle}</Text> : null}
        </VStack>
      </HStack>
      {children}
    </Box>
  );
}

function UnavailableLine() {
  return (
    <Text fontSize="sm" color="textSecondary" fontStyle="italic">
      Bu bölüm şu an yüklenemedi.
    </Text>
  );
}

// ──────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────

function SummaryHero({ s }: { s: MentorSummaryResp }) {
  const acc = s.stats.accuracy;
  const streakBadge = s.stats.streak >= 3 ? '🔥' : null;
  return (
    <Box
      bg="bgCard"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="2xl"
      boxShadow="cardElevated"
      p={4}
    >
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="center">
          <HStack spacing={2}>
            <Text fontSize="2xl" lineHeight="1" color="coachAccent" fontWeight="bold">
              {ICON.mentor}
            </Text>
            <Heading size="md" color="textPrimary">Dijital Mentor</Heading>
          </HStack>
          <PlanBadge tier={s.tier} />
        </HStack>

        <Box>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="11px" color="textMuted" textTransform="uppercase" letterSpacing="0.05em" fontWeight="semibold">
              Doğruluk
            </Text>
            <Text fontSize="sm" fontWeight="bold" color="textPrimary">%{acc}</Text>
          </HStack>
          <Progress
            value={acc}
            size="sm"
            colorScheme={acc < 50 ? 'red' : acc < 75 ? 'yellow' : 'green'}
            borderRadius="full"
          />
        </Box>

        <SimpleGrid columns={2} spacing={2}>
          <HeroStat label="Toplam Soru" value={s.stats.totalAttempts} />
          <HeroStat label="Son 7 gün" value={s.stats.last7days} />
          <HeroStat
            label="Seri"
            value={`${s.stats.streak}g${streakBadge ? ` ${streakBadge}` : ''}`}
          />
          <HeroStat
            label="Antrenör"
            value={`${s.srs.box} kayıt · ${s.srs.due} tekrar`}
          />
        </SimpleGrid>
      </VStack>
    </Box>
  );
}

function HeroStat({ label, value }: { label: string; value: number | string }) {
  return (
    <Box
      bg="bgSecondary"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="lg"
      px={2.5}
      py={2}
    >
      <Text
        fontSize="10px"
        color="textMuted"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        {label}
      </Text>
      <Text fontWeight="bold" fontSize="md" color="textPrimary" lineHeight="1.1" mt={0.5} noOfLines={1}>
        {value}
      </Text>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────
// Coach
// ──────────────────────────────────────────────────────────

function CoachBlock({ coach }: { coach: MentorCoachResp }) {
  return (
    <VStack align="stretch" spacing={2.5}>
      <HStack spacing={2} fontSize="11px" color="textSecondary">
        <Badge variant="subtle" colorScheme="blue" textTransform="none">
          Bugün {coach.todayCount}
        </Badge>
        <Badge variant="subtle" colorScheme="gray" textTransform="none">
          Dün {coach.yesterdayCount}
        </Badge>
        <Badge variant="subtle" colorScheme="green" textTransform="none">
          Tekrar {coach.srsDue}
        </Badge>
      </HStack>
      <VStack align="stretch" spacing={1.5}>
        {coach.tips.map((tip, idx) => (
          <Box
            key={idx}
            bg="bgSecondary"
            borderWidth="1px"
            borderColor="borderPrimary"
            borderRadius="md"
            p={2.5}
          >
            <Text fontSize="sm" color="textPrimary" lineHeight="1.4">
              {tip.text}
            </Text>
          </Box>
        ))}
        {coach.tips.length === 0 ? (
          <Text fontSize="sm" color="textSecondary" fontStyle="italic">
            Bugün için özel öneri yok.
          </Text>
        ) : null}
      </VStack>
    </VStack>
  );
}

// ──────────────────────────────────────────────────────────
// Study Pattern
// ──────────────────────────────────────────────────────────

function PatternBlock({ p }: { p: MentorStudyPatternResp }) {
  const max14 = Math.max(1, ...p.days.map((d) => d.count));
  const dayLabels = ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'];
  const last7 = p.days.slice(-7);

  return (
    <VStack align="stretch" spacing={3}>
      <SimpleGrid columns={2} spacing={2}>
        <PatternStat label="14 gün toplam" value={p.total14} />
        <PatternStat label="Ort./gün" value={p.avgPerDay} />
        <PatternStat label="En yoğun gün" value={`${p.peakDay.date.slice(5)} · ${p.peakDay.count}`} />
        <PatternStat label="En yoğun saat" value={`${String(p.peakHour.hour).padStart(2, '0')}:00 · ${p.peakHour.count}`} />
      </SimpleGrid>

      {/* 14-gün bar grid */}
      <Box>
        <Text
          fontSize="10px"
          color="textMuted"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.05em"
          mb={1.5}
        >
          14 Gün Trend
        </Text>
        <HStack spacing={1} align="end" h="44px">
          {p.days.map((d) => {
            const ratio = d.count / max14;
            const heightPct = Math.max(6, ratio * 100);
            return (
              <Box
                key={d.date}
                flex="1"
                h={`${heightPct}%`}
                bg={d.count > 0 ? 'accent' : 'borderPrimary'}
                opacity={d.count > 0 ? 0.4 + ratio * 0.6 : 0.5}
                borderRadius="sm"
                title={`${d.date} · ${d.count}`}
              />
            );
          })}
        </HStack>
      </Box>

      {/* Last 7 days heat */}
      <Box>
        <Text
          fontSize="10px"
          color="textMuted"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.05em"
          mb={1.5}
        >
          Son 7 Gün
        </Text>
        <HStack spacing={1.5}>
          {last7.map((d) => {
            const ratio = Math.min(1, d.count / 30);
            return (
              <VStack key={d.date} spacing={1} flex="1">
                <Box
                  w="full"
                  h="22px"
                  borderRadius="sm"
                  bg={d.count > 0 ? 'accent' : 'bgSecondary'}
                  opacity={d.count > 0 ? 0.3 + ratio * 0.7 : 0.6}
                  borderWidth="1px"
                  borderColor="borderPrimary"
                  title={`${d.date} · ${d.count}`}
                />
                <Text fontSize="9px" color="textMuted">
                  {dayLabels[d.weekday]}
                </Text>
              </VStack>
            );
          })}
        </HStack>
      </Box>

      {/* 24-saat aktivite */}
      <Box>
        <Text
          fontSize="10px"
          color="textMuted"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.05em"
          mb={1.5}
        >
          24 Saat Aktivite
        </Text>
        <HourSparkline hours={p.hours} />
        <Flex justify="space-between" pt={1}>
          <Text fontSize="9px" color="textMuted">00</Text>
          <Text fontSize="9px" color="textMuted">06</Text>
          <Text fontSize="9px" color="textMuted">12</Text>
          <Text fontSize="9px" color="textMuted">18</Text>
          <Text fontSize="9px" color="textMuted">23</Text>
        </Flex>
      </Box>
    </VStack>
  );
}

function PatternStat({ label, value }: { label: string; value: number | string }) {
  return (
    <Box
      bg="bgSecondary"
      borderWidth="1px"
      borderColor="borderPrimary"
      borderRadius="md"
      px={2.5}
      py={1.5}
    >
      <Text
        fontSize="9px"
        color="textMuted"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="0.05em"
        noOfLines={1}
      >
        {label}
      </Text>
      <Text fontWeight="bold" fontSize="13px" color="textPrimary" lineHeight="1.2" noOfLines={1}>
        {value}
      </Text>
    </Box>
  );
}

function HourSparkline({ hours }: { hours: { hour: number; count: number }[] }) {
  const max = Math.max(1, ...hours.map((h) => h.count));
  return (
    <HStack spacing={0.5} align="end" h="32px">
      {hours.map((h) => {
        const ratio = h.count / max;
        const heightPct = Math.max(8, ratio * 100);
        return (
          <Box
            key={h.hour}
            flex="1"
            h={`${heightPct}%`}
            bg={h.count > 0 ? 'coachAccent' : 'borderPrimary'}
            opacity={h.count > 0 ? 0.4 + ratio * 0.6 : 0.4}
            borderRadius="sm"
            title={`${String(h.hour).padStart(2, '0')}:00 · ${h.count}`}
          />
        );
      })}
    </HStack>
  );
}

// ──────────────────────────────────────────────────────────
// Forgetting Curve
// ──────────────────────────────────────────────────────────

function ForgettingBlock({ f, onReview }: { f: MentorForgettingResp; onReview: () => void }) {
  return (
    <VStack align="stretch" spacing={2}>
      <HStack justify="space-between" fontSize="11px" color="textMuted">
        <Text fontStyle="italic">Engine: {f.engine}</Text>
        {f.upcoming48h !== undefined ? (
          <Badge colorScheme="purple" textTransform="none">+48h: {f.upcoming48h}</Badge>
        ) : null}
      </HStack>
      <Text fontSize="sm" fontWeight="semibold" color="textPrimary">
        Tekrar zamanı geçmiş: {f.overdue.length}
      </Text>
      {f.overdue.length === 0 ? (
        <Text fontSize="sm" color="textSecondary" fontStyle="italic">
          Geciken tekrar yok. Düzenli çalışma sürüyor.
        </Text>
      ) : (
        <VStack align="stretch" spacing={1}>
          {f.overdue.slice(0, 6).map((o, i) => (
            <HStack
              key={`${o.srsId}-${i}`}
              bg="bgSecondary"
              borderWidth="1px"
              borderColor="borderPrimary"
              borderRadius="md"
              px={2.5}
              py={1.5}
              spacing={2}
              align="start"
            >
              <Badge colorScheme="orange" textTransform="none" fontSize="10px">
                Kutu {o.boxNumber}
              </Badge>
              <Text fontSize="12px" color="textPrimary" noOfLines={2} flex="1">
                {(o.title || '').slice(0, 80) || '(başlıksız)'}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}
      {f.overdue.length > 0 ? (
        <Button size="sm" colorScheme="brand" onClick={onReview}>
          Şimdi Tekrar Et
        </Button>
      ) : null}
    </VStack>
  );
}

// ──────────────────────────────────────────────────────────
// Exam Readiness
// ──────────────────────────────────────────────────────────

function ExamReadinessBlock({ r, onUpgrade }: { r: MentorExamReadinessResp; onUpgrade: () => void }) {
  if (r.mode === 'locked') {
    return <LockedBlock message={r.message || 'Bu özellik Silver ve üzeri planlarda açık.'} onUpgrade={onUpgrade} />;
  }
  const score = r.score ?? 0;
  return (
    <VStack align="stretch" spacing={2}>
      <HStack justify="space-between" align="end">
        <Heading size="lg" color="textPrimary" lineHeight="1">
          {score}<Text as="span" fontSize="md" color="textMuted">/100</Text>
        </Heading>
        {r.confidence ? (
          <Badge
            colorScheme={r.confidence === 'high' ? 'green' : r.confidence === 'medium' ? 'blue' : 'gray'}
            textTransform="none"
          >
            {labelConfidence(r.confidence)}
          </Badge>
        ) : null}
      </HStack>
      <Progress
        value={score}
        size="sm"
        colorScheme={score < 40 ? 'red' : score < 70 ? 'yellow' : 'green'}
        borderRadius="full"
      />
      {r.attemptCount ? (
        <Text fontSize="11px" color="textMuted">
          {r.attemptCount} soru üzerinden hesaplandı.
        </Text>
      ) : null}
      {r.mode === 'preview' ? (
        <Text fontSize="11px" color="textSecondary" fontStyle="italic">
          Önizleme · Gold ile detay açılır.
        </Text>
      ) : null}
    </VStack>
  );
}

function labelConfidence(c: 'low' | 'medium' | 'high'): string {
  if (c === 'low') return 'Düşük (az veri)';
  if (c === 'medium') return 'Orta';
  return 'Yüksek';
}

// ──────────────────────────────────────────────────────────
// Peer Compare
// ──────────────────────────────────────────────────────────

function PeerBlock({ p, onUpgrade }: { p: MentorPeerCompareResp; onUpgrade: () => void }) {
  if (!p.available) {
    return <LockedBlock message={p.message || 'Bu özellik Gold ve Diamond planlarda açık.'} onUpgrade={onUpgrade} />;
  }
  if (p.myAccuracy == null) {
    return (
      <Text fontSize="sm" color="textSecondary" fontStyle="italic">
        {p.message || 'Yetersiz veri. Daha fazla soru çöz.'}
      </Text>
    );
  }
  const me = p.myAccuracy;
  const peer = p.peerAccuracy ?? 0;
  const delta = p.delta ?? 0;
  return (
    <VStack align="stretch" spacing={2.5}>
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="11px" color="textMuted" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.05em">Sen</Text>
          <Text fontSize="sm" fontWeight="bold" color="textPrimary">%{me}</Text>
        </HStack>
        <Progress value={me} size="sm" colorScheme="blue" borderRadius="full" />
      </Box>
      <Box>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="11px" color="textMuted" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.05em">Akran</Text>
          <Text fontSize="sm" fontWeight="bold" color="textPrimary">%{peer}</Text>
        </HStack>
        <Progress value={peer} size="sm" colorScheme="purple" borderRadius="full" />
      </Box>
      <HStack justify="space-between">
        <Text fontSize="sm" color="textSecondary">Fark</Text>
        <Badge
          colorScheme={delta > 0 ? 'green' : delta < 0 ? 'red' : 'gray'}
          textTransform="none"
          fontSize="sm"
        >
          {delta > 0 ? '+' : ''}{delta}%
        </Badge>
      </HStack>
      {p.myAttempts ? (
        <Text fontSize="11px" color="textMuted" fontStyle="italic">
          {p.myAttempts} soru üzerinden hesaplandı.
        </Text>
      ) : null}
    </VStack>
  );
}

// ──────────────────────────────────────────────────────────
// Burnout
// ──────────────────────────────────────────────────────────

function BurnoutBlock({ b, onUpgrade }: { b: MentorBurnoutResp; onUpgrade: () => void }) {
  if (!b.available) {
    return <LockedBlock message={b.message || 'Bu özellik Diamond planda açık.'} onUpgrade={onUpgrade} />;
  }
  const signalScheme: Record<string, string> = {
    normal: 'green',
    drop: 'orange',
    accuracy_drop: 'orange',
    overload: 'red',
  };
  const scheme = signalScheme[b.signal || 'normal'] || 'gray';
  return (
    <VStack align="stretch" spacing={2}>
      {b.message ? (
        <HStack>
          <Badge colorScheme={scheme} textTransform="none">
            {b.signal === 'normal' ? 'Normal' : b.signal === 'drop' ? 'Düşüş' : b.signal === 'accuracy_drop' ? 'Doğruluk düşüşü' : b.signal === 'overload' ? 'Aşırı yük' : 'Sinyal'}
          </Badge>
          <Text fontSize="sm" color="textPrimary" fontWeight="semibold" noOfLines={2}>
            {b.message}
          </Text>
        </HStack>
      ) : null}
      <SimpleGrid columns={2} spacing={2}>
        <PatternStat
          label="Son 7 gün"
          value={b.recent ? `${b.recent.count} · %${b.recent.accuracy}` : '–'}
        />
        <PatternStat
          label="Önceki 7 gün"
          value={b.prior ? `${b.prior.count} · %${b.prior.accuracy}` : '–'}
        />
      </SimpleGrid>
    </VStack>
  );
}

// ──────────────────────────────────────────────────────────
// Locked block
// ──────────────────────────────────────────────────────────

function LockedBlock({ message, onUpgrade }: { message: string; onUpgrade: () => void }) {
  return (
    <VStack align="stretch" spacing={2}>
      <HStack
        bg="bgSecondary"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor="borderPrimary"
        borderRadius="md"
        p={3}
        spacing={2}
      >
        <Text fontSize="lg" color="textMuted" fontWeight="bold">{ICON.lock}</Text>
        <Text fontSize="sm" color="textSecondary" lineHeight="1.4" flex="1">
          {message}
        </Text>
      </HStack>
      <Wrap>
        <WrapItem>
          <Button size="sm" colorScheme="brand" onClick={onUpgrade}>
            {BRAND.copy.upgrade}
          </Button>
        </WrapItem>
      </Wrap>
    </VStack>
  );
}
